import { getCookie } from '../utils/cookie'
import { isLoginDomain } from '../utils/environment'

// Flag to prevent multiple modal windows from showing
let isSessionExpiredModalShown = false;

// Pre-import action creator to avoid async delay
let showSessionExpiredModalAction = null;
let sessionExpiredSliceModule = null;

// Synchronously import and cache the slice module
const initSessionExpiredModal = () => {
  if (!sessionExpiredSliceModule && typeof window !== 'undefined' && window.__REDUX_STORE__) {
    import("../redux/features/auth/sessionExpiredSlice")
      .then((sliceModule) => {
        sessionExpiredSliceModule = sliceModule;
        showSessionExpiredModalAction = sliceModule.showSessionExpiredModal;
      })
      .catch((err) => {
        console.error("Failed to pre-import sessionExpiredSlice:", err);
      });
  }
};

// Initialize on module load
if (typeof window !== 'undefined') {
  // Small delay to ensure store is ready
  setTimeout(initSessionExpiredModal, 100);
  // Also try immediately in case store is already available
  initSessionExpiredModal();
}

function queryParams(source) {
  const array = [];

  for (const key in source) {
    if (source[key] !== null && source[key] !== undefined) {
      array.push(
        encodeURIComponent(key) + "=" + encodeURIComponent(source[key])
      );
    }
  }
  return array.join("&");
}

export default async function callApi(
  endpoint,
  method = "GET",
  payload = {},
  params
) {
  // const token = localStorage.getItem("api_key");
  const token2 = getCookie("API-KEY");
  const token = getCookie("api_key");
  	// Get URL from environment variable
  const url = process.env.REACT_APP_API_URL || "";
  	// Determine if this is a development environment
  const isDev = url.includes('dev');
  const isLocalHost = window.location.origin.includes("localhost");
  // console.log('isLocalHost', isLocalHost)
  const baseUrl = isDev ? process.env.REACT_APP_API_URL : process.env.REACT_APP_API_URL;

  const apiHeaders =
    (token || token2) && endpoint !== "/user/login" ? { "api-key": token || token2 } : {};
  const { extraHeaders = {}, contentType = "" } = params ?? {};

  let fullUrl = `${baseUrl}/api/1${endpoint}`;

  if (method === "GET" && Object.keys(payload).length) {
    fullUrl += `?${queryParams(payload)}`;
  }
  // console.log('apiHeaders', apiHeaders)
  const body =
    contentType === "multipart/form-data" ? payload : JSON.stringify(payload);

  const options = {
    method,
    headers: {
      Accept: "application/json",
      ...(contentType ? {} : { "Content-Type": "application/json" }),
      ...apiHeaders,
      ...extraHeaders,
    },
    body: method !== "GET" ? body : null,
  };

  try {
    const response = await fetch(fullUrl, options);
    // Helper function to show session expired modal
    const showSessionExpiredModal = () => {
      // Use global store reference to avoid circular dependencies
      if (typeof window !== 'undefined' && window.__REDUX_STORE__) {
        // Try to use pre-imported action first
        if (showSessionExpiredModalAction) {
          try {
            window.__REDUX_STORE__.dispatch(showSessionExpiredModalAction());
          } catch (dispatchError) {
            console.error("Failed to dispatch session expired modal action:", dispatchError);
          }
        } else if (sessionExpiredSliceModule) {
          // Use cached module if available
          try {
            showSessionExpiredModalAction = sessionExpiredSliceModule.showSessionExpiredModal;
            window.__REDUX_STORE__.dispatch(sessionExpiredSliceModule.showSessionExpiredModal());
          } catch (dispatchError) {
            console.error("Failed to dispatch session expired modal action:", dispatchError);
          }
        } else {
          // Fallback to async import - but mark flag immediately to prevent multiple imports
          isSessionExpiredModalShown = true;
          import("../redux/features/auth/sessionExpiredSlice")
            .then((sliceModule) => {
              sessionExpiredSliceModule = sliceModule;
              showSessionExpiredModalAction = sliceModule.showSessionExpiredModal;
              if (window.__REDUX_STORE__) {
                window.__REDUX_STORE__.dispatch(sliceModule.showSessionExpiredModal());
              }
            })
            .catch((importError) => {
              console.error("Failed to show session expired modal:", importError);
              // Reset flag on error so it can be retried
              isSessionExpiredModalShown = false;
            });
        }
      } else {
        console.warn("Redux store not available for session expired modal");
      }
    };
    
    // Check for 401 Unauthorized status - show modal only once on first 401 response
    // Don't show modal for login/activate endpoints or on login/invitation pages - it's expected to return 401 for invalid credentials
    const pathname = typeof window !== 'undefined' ? window.location.pathname : '';
    const isLoginPage = typeof window !== 'undefined' && (
      pathname.includes('/login') ||
      pathname.includes('/manager') ||
      pathname.includes('/signup') ||
      pathname.includes('/forgot') ||
      pathname.includes('/activation') ||
      pathname.includes('/confirm-2fa') ||
      pathname.startsWith('/join/accept') ||
      pathname.startsWith('/invitation') ||
      pathname.startsWith('/invite/accept') ||
      pathname.startsWith('/invite/profile') ||
      // Root path on login domain (e.g. https://login.dev.lappa.org/) redirects to /login but pathname is "/" until redirect
      ((pathname === '/' || pathname === '') && isLoginDomain())
    );
    const isActivateEndpoint = endpoint === '/user/activate';
    const isInvitationEndpoint = endpoint && endpoint.includes('/invitation/validate');

    if (response.status === 401 && endpoint !== "/user/login" && !isActivateEndpoint && !isInvitationEndpoint && !isLoginPage) {
      // Use atomic check-and-set to prevent race conditions with multiple simultaneous requests
      if (!isSessionExpiredModalShown) {
        isSessionExpiredModalShown = true;
        showSessionExpiredModal();
      }
    }
    
    return response;
  } catch (err) {
    console.error("API call error:", err);
    // Return a mock response object to prevent uncaught errors
    return new Response(JSON.stringify({ error: err.message }), {
      status: 500,
      statusText: 'Network Error',
      headers: { 'Content-Type': 'application/json' }
    });
  }
}

// Export function to reset the flag (useful for testing or after login)
export function resetSessionExpiredFlag() {
  isSessionExpiredModalShown = false;
}
